import { useMemo, useState } from "react";
import { formatDateCell, formatMissingSettings, startCase } from "../lib/utils";

export function useNotifications({
  canManageBulkImports,
  context,
  followUps,
  integrationDiagnostics,
  lastImportAlert,
  lastSyncAlert,
}) {
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [dismissedNotificationIds, setDismissedNotificationIds] = useState(
    () => new Set(),
  );

  const notifications = useMemo(() => {
    const nextNotifications = [];

    if (lastImportAlert) {
      nextNotifications.push({
        actionLabel: "Open Uploads",
        category: "import_issue",
        ...lastImportAlert,
      });
    }

    if (canManageBulkImports && lastSyncAlert) {
      nextNotifications.push({
        actionLabel: "Open Sync Panel",
        category: "sync_issue",
        ...lastSyncAlert,
      });
    }

    const facebookSetupIssue =
      canManageBulkImports &&
      integrationDiagnostics?.facebook?.enabled &&
      !integrationDiagnostics?.facebook?.configured
        ? formatMissingSettings(integrationDiagnostics?.facebook?.missing_settings)
        : "";
    if (facebookSetupIssue) {
      nextNotifications.push({
        id: "facebook-sync-setup",
        category: "sync_issue",
        tone: "warning",
        title: "Facebook sync needs setup",
        description: facebookSetupIssue,
        actionLabel: "Open Sync Panel",
      });
    } else if (canManageBulkImports && integrationDiagnostics?.error) {
      nextNotifications.push({
        id: "integration-diagnostics-error",
        category: "sync_issue",
        tone: "danger",
        title: "Integration diagnostics failed",
        description: integrationDiagnostics.error,
        actionLabel: "Retry in CRM",
      });
    }

    const phoneRevealAlerts = Array.isArray(context?.phone_reveal_alerts)
      ? context.phone_reveal_alerts
      : [];
    phoneRevealAlerts.slice(0, 5).forEach((alert) => {
      nextNotifications.push({
        id: `phone-reveal-${alert.id}`,
        category: "phone_reveal",
        tone: "warning",
        title: `${alert.agent_name || "An agent"} revealed a phone number`,
        description: `${alert.phone_number || "Phone unavailable"} · ${alert.lead_name || "Unknown lead"}${alert.client_id ? ` · ${alert.client_id}` : ""}`,
        meta: formatDateCell(alert.occurred_at),
        actionLabel: "Open Agents",
      });
    });
    if (phoneRevealAlerts.length > 5) {
      nextNotifications.push({
        id: "phone-reveal-summary",
        category: "phone_reveal",
        tone: "warning",
        title: `${phoneRevealAlerts.length} phone numbers revealed in the last 24 hours`,
        description: "Open the agents table to inspect the full reveal log by agent.",
        actionLabel: "Open Agents",
      });
    }

    const now = new Date();
    const todayKey = now.toDateString();
    const urgentFollowUps = [...(followUps || [])]
      .filter((followUp) => {
        if (!followUp?.scheduled_for) return false;
        const scheduledAt = new Date(followUp.scheduled_for);
        return scheduledAt < now || scheduledAt.toDateString() === todayKey;
      })
      .sort(
        (left, right) =>
          new Date(left.scheduled_for).getTime() -
          new Date(right.scheduled_for).getTime(),
      );

    urgentFollowUps.slice(0, 3).forEach((followUp) => {
      const scheduledAt = new Date(followUp.scheduled_for);
      const isOverdue = scheduledAt < now && followUp.lead_status === "new";
      nextNotifications.push({
        id: `follow-up-${followUp.id}`,
        category: "follow_up",
        tone: isOverdue ? "danger" : "info",
        title: `${isOverdue ? "Overdue" : "Today"} follow-up for ${followUp.lead_full_name}`,
        description: `${formatDateCell(followUp.scheduled_for)} · ${followUp.lead_city_name || "Unspecified City"} / ${followUp.lead_region_name || "Unspecified Region"}`,
        meta: `${followUp.lead_client_id} · ${startCase(followUp.lead_status)}`,
        actionLabel: "Open Follow-ups",
      });
    });

    if (urgentFollowUps.length > 3) {
      nextNotifications.push({
        id: "follow-up-summary",
        category: "follow_up",
        tone: "info",
        title: `${urgentFollowUps.length} urgent follow-ups need attention`,
        description: "Open the follow-up board to review the full pending queue.",
        actionLabel: "Open Follow-ups",
      });
    }

    return nextNotifications;
  }, [
    canManageBulkImports,
    context?.phone_reveal_alerts,
    followUps,
    integrationDiagnostics,
    lastImportAlert,
    lastSyncAlert,
  ]);

  function dismissNotification(id) {
    setDismissedNotificationIds((prev) => {
      const next = new Set(prev);
      next.add(id);
      return next;
    });
  }

  const visibleNotifications = notifications.filter(
    (n) => !dismissedNotificationIds.has(n.id),
  );
  const unreadNotificationCount = visibleNotifications.length;

  return {
    dismissedNotificationIds,
    dismissNotification,
    notifications,
    notificationsOpen,
    setDismissedNotificationIds,
    setNotificationsOpen,
    unreadNotificationCount,
    visibleNotifications,
  };
}
